import React from 'react';
import { fetchDashboardConfig, saveDashboardConfig } from '../apiDashboardConfig';
import {
  Card,
  Row,
  Col,
  Space,
  Button,
  Input,
  Spin,
  Empty,
  Tag,
  Tooltip,
  Popconfirm,
  Modal,
  Grid,
  Statistic,
  Divider,
  Alert,
  Popover,
} from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  DeleteOutlined,
  UpOutlined,
  DownOutlined,
  ReloadOutlined,
  CloudServerOutlined,
  BgColorsOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { useBreakpoint } = Grid;

const INDUSTRIAL_IPS = [
  { ip: '192.168.0.138', defaultName: 'Servidor Principal (138)' },
  { ip: '192.168.0.139', defaultName: 'Nodo Secundario (139)' },
  { ip: '192.168.0.140', defaultName: 'Nodo Industrial (140)' },
  { ip: '192.168.0.144', defaultName: 'Nodo PLC (144)' },
  { ip: '192.168.0.153', defaultName: 'Centro IoT (153)' },
];



function prettifyMetricLabel(key) {
  return String(key || '')
    .replace(/^status_/i, '')
    .replace(/^overview_/i, '')
    .replace(/[._]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function flattenPrimitiveMetrics(input, prefix = '', out = {}) {
  if (input === null || input === undefined) return out;
  const kind = typeof input;
  if (kind === 'number' || kind === 'string' || kind === 'boolean') {
    if (prefix) out[prefix] = input;
    return out;
  }
  if (Array.isArray(input)) {
    input.forEach((item, idx) => {
      flattenPrimitiveMetrics(item, prefix ? `${prefix}.${idx}` : String(idx), out);
    });
    return out;
  }
  if (kind === 'object') {
    Object.entries(input).forEach(([k, v]) => {
      const nextPrefix = prefix ? `${prefix}.${k}` : k;
      flattenPrimitiveMetrics(v, nextPrefix, out);
    });
  }
  return out;
}

function findMetricEntry(flatMap, patterns) {
  return Object.entries(flatMap).find(([key]) => patterns.some((p) => p.test(key))) || null;
}

function humanBytes(num) {
  if (!Number.isFinite(num)) return String(num);
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = num;
  let idx = 0;
  while (value >= 1024 && idx < units.length - 1) {
    value /= 1024;
    idx += 1;
  }
  return `${value.toFixed(value < 10 && idx > 0 ? 2 : 1)} ${units[idx]}`;
}

function formatMetricFromEntry(entry) {
  if (!entry) return { value: 'N/A', unit: '' };
  const [key, raw] = entry;
  const isPercent = /(pct|percent|percentage|usage|used_pct|free_pct)/i.test(key);
  const isBytes = /(bytes|_bytes|byte)/i.test(key);

  if (typeof raw === 'number') {
    if (isBytes) return { value: humanBytes(raw), unit: '' };
    if (isPercent) return { value: raw.toFixed(1), unit: '%' };
    return { value: raw.toFixed(raw >= 100 ? 0 : 2), unit: '' };
  }

  return { value: String(raw), unit: '' };
}

function pickMetricByPriority(flatMap, patterns) {
  for (const pattern of patterns) {
    const found = findMetricEntry(flatMap, [pattern]);
    if (found) return found;
  }
  return null;
}

function MetricDisplay({ label, value, unit = '', color = '#0f172a', compact = false }) {
  return (
    <div style={{
      marginBottom: compact ? 0 : 6,
      paddingBottom: compact ? 0 : 4,
      borderBottom: compact ? 'none' : '1px solid rgba(15,23,42,0.06)'
    }}>
      <div style={{
        fontSize: compact ? 8 : 10,
        color: '#64748b',
        fontWeight: 600,
        marginBottom: 1,
        textTransform: 'uppercase',
        letterSpacing: 0.2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
        {label}
      </div>
      <div style={{
        fontSize: compact ? 12 : 15,
        fontWeight: 800,
        color,
        lineHeight: 1.05,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
        {value === null || value === undefined ? '--' : value}
        {unit && <span style={{ fontSize: compact ? 8.5 : 10, marginLeft: 2, color: '#94a3b8' }}>{unit}</span>}
      </div>
    </div>
  );
}

function CompactTile({ label, value, unit = '', bg = '#f8fafc', color = '#0f172a' }) {
  return (
    <div style={{
      background: bg,
      padding: '2px 4px',
      borderRadius: 5,
      minWidth: 0,
      border: '1px solid rgba(15,23,42,0.04)'
    }}>
      <div style={{
        fontSize: 7,
        color: '#64748b',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 0.15,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        lineHeight: 0.9
      }}>{label}</div>
      <div style={{
        fontSize: 12,
        fontWeight: 800,
        color,
        lineHeight: 0.95,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}>
        {value ?? '--'}
        {unit && <span style={{ fontSize: 8, marginLeft: 1, color: '#94a3b8', fontWeight: 600 }}>{unit}</span>}
      </div>
    </div>
  );
}

const COLOR_PRESETS = ['#f8fafc', '#fef3c7', '#dcfce7', '#dbeafe', '#fee2e2', '#ede9fe', '#fce7f3', '#fff7ed', '#cffafe', '#f0fdf4'];

function MonitoringCard({ ipConfig, onNameChange, onDelete, expanded, onExpandChange, onColorChange }) {
  const [loading, setLoading] = React.useState(false);
  const [metrics, setMetrics] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const [editName, setEditName] = React.useState(ipConfig.displayName || ipConfig.defaultName);
  const [error, setError] = React.useState(null);
  const [colorPickerOpen, setColorPickerOpen] = React.useState(false);
  const [cardColor, setCardColor] = React.useState(ipConfig.color || '#f8fafc');
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const authHeader = React.useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const loadMetrics = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get('/api/ops/industrial/node-status', {
        timeout: 7000,
        headers: authHeader(),
        params: { ip: ipConfig.ip },
      });

      const metrics = {
        system: response.data?.system || null,
        services: response.data?.services || null,
        network: response.data?.network || null,
        host: response.data?.host || null,
        mqtt: response.data?.mqtt || null,
        extra: response.data?.extra || null,
        statusRaw: response.data?.status_raw || null,
        overviewRaw: response.data?.overview_raw || null,
      };

      const hasData = !!(metrics.system || metrics.host || metrics.services || metrics.network || metrics.mqtt);
      if (!hasData) {
        setError('Sin datos disponibles. Verifica que la IP sea accesible.');
      } else {
        setMetrics(metrics);
      }
    } catch (err) {
      const backendMsg = err?.response?.data?.error;
      setMetrics(null);
      setError(backendMsg || err.message || 'No se pudieron cargar métricas');
    } finally {
      setLoading(false);
    }
  }, [authHeader, ipConfig.ip]);

  React.useEffect(() => {
    setCardColor(ipConfig.color || '#f8fafc');
  }, [ipConfig.color]);

  React.useEffect(() => {
    setEditName(ipConfig.displayName || ipConfig.defaultName);
  }, [ipConfig.displayName, ipConfig.defaultName]);

  React.useEffect(() => {
    loadMetrics();
    const interval = setInterval(loadMetrics, 30000);
    return () => clearInterval(interval);
  }, [loadMetrics]);

  const handleSaveName = () => {
    onNameChange(ipConfig.ip, editName, cardColor);
    setEditing(false);
  };

  const handleColorPick = (color) => {
    setCardColor(color);
    onColorChange(ipConfig.ip, color);
  };

  const isOnline = !!(metrics?.system || metrics?.host || metrics?.services || metrics?.network || metrics?.mqtt);
  const flattened = React.useMemo(
    () => flattenPrimitiveMetrics({ system: metrics?.system, host: metrics?.host, extra: metrics?.extra }),
    [metrics]
  );
  const cpuTempEntry = findMetricEntry(flattened, [/cpu.*temp/i, /temp.*cpu/i, /soc.*temp/i, /thermal/i]);
  const ramUsageEntry = pickMetricByPriority(flattened, [
    /status_history\.points\.\d+\.ram_pct/i,
    /status_memory\.used_percent/i,
    /memory\.used_percent/i,
    /free_mem_pct/i,
    /ram.*(used_pct|used_percent|pct|percent|usage)/i,
    /mem(or(y|ia))?.*(used_pct|used_percent|pct|percent|usage)/i,
    /status_memory\.used_mb/i,
    /memory\.used_mb/i,
  ]);
  const ramFreeEntry = findMetricEntry(flattened, [/ram.*free/i, /mem(or(y|ia))?.*free/i]);
  const diskUsageEntry = pickMetricByPriority(flattened, [
    /status_history\.points\.\d+\.disk_pct/i,
    /status_storage\.root_used_percent/i,
    /status_storage\.inode_used_percent/i,
    /(disk|storage|fs|filesystem).*(used_pct|used_percent|pct|percent|usage)/i,
    /status_storage\.root_used_mb/i,
    /(disk|storage|fs|filesystem).*(used_mb|free_mb|total_mb)/i,
  ]);
  const diskFreeEntry = findMetricEntry(flattened, [/disk.*free/i, /(storage|fs|filesystem).*free/i]);
  const cpuTemp = formatMetricFromEntry(cpuTempEntry);
  const ramDisplay = formatMetricFromEntry(ramUsageEntry || ramFreeEntry);
  const diskDisplay = formatMetricFromEntry(diskUsageEntry || diskFreeEntry);
  const advancedPairs = Object.entries(flattened)
    .filter(([key]) => /(temp|cpu|ram|mem|swap|disk|storage|fs|filesystem|load|thermal)/i.test(key))
    .slice(0, 18);

  const colorPickerContent = (
    <div style={{ width: 220 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 10 }}>
        {COLOR_PRESETS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => handleColorPick(c)}
            style={{
              width: 32, height: 32, borderRadius: 8, cursor: 'pointer',
              background: c,
              border: cardColor === c ? '2px solid #0f172a' : '1px solid #e5e7eb',
              padding: 0,
            }}
            aria-label={`Color ${c}`}
          />
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, color: '#64748b' }}>Personalizado:</span>
        <input
          type="color"
          value={cardColor}
          onChange={(e) => handleColorPick(e.target.value)}
          style={{ width: 40, height: 32, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
        />
      </div>
    </div>
  );

  return (
    <Card
      size='small'
      className="industrial-card glass-card"
      style={{
        borderRadius: 12,
        borderLeft: `4px solid ${isOnline ? '#16a34a' : '#ef4444'}`,
        background: cardColor,
        transition: 'background 0.2s',
        overflow: 'hidden',
      }}
      bodyStyle={{ padding: isMobile ? 8 : 12 }}
      headStyle={{ padding: isMobile ? '0 8px' : '0 10px', minHeight: isMobile ? 36 : 40 }}
      title={
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, width: '100%', minWidth: 0 }}>
          <CloudServerOutlined style={{ color: isOnline ? '#16a34a' : '#ef4444', flexShrink: 0 }} />
          {editing ? (
            <Input
              size='small'
              value={editName}
              autoFocus
              onChange={(e) => setEditName(e.target.value)}
              onPressEnter={handleSaveName}
              onClick={(e) => e.stopPropagation()}
              style={{ flex: '1 1 120px', minWidth: 120, maxWidth: 240 }}
            />
          ) : (
            <strong
              title={ipConfig.displayName || ipConfig.defaultName}
              style={{
                flex: '1 1 auto',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: isMobile ? 13 : 14,
              }}
            >
              {ipConfig.displayName || ipConfig.defaultName}
            </strong>
          )}
          <Tag color={isOnline ? 'green' : 'red'} style={{ marginInlineEnd: 0 }}>
            {isOnline ? 'En línea' : 'Offline'}
          </Tag>
          <div style={{ display: 'flex', gap: 4, marginLeft: 'auto', flexWrap: 'wrap' }}>
            <Popover
              content={colorPickerContent}
              title="Color de tarjeta"
              trigger="click"
              open={colorPickerOpen}
              onOpenChange={setColorPickerOpen}
            >
              <Tooltip title="Color">
                <Button
                  size="small"
                  icon={<BgColorsOutlined />}
                  style={{ background: cardColor, borderColor: '#cbd5e1' }}
                />
              </Tooltip>
            </Popover>
            {editing ? (
              <>
                <Tooltip title="Guardar"><Button size='small' type='primary' icon={<SaveOutlined />} onClick={handleSaveName} /></Tooltip>
                <Tooltip title="Cancelar"><Button size='small' icon={<CloseOutlined />} onClick={() => { setEditing(false); setEditName(ipConfig.displayName || ipConfig.defaultName); }} /></Tooltip>
              </>
            ) : (
              <>
                <Tooltip title="Editar nombre"><Button size='small' icon={<EditOutlined />} onClick={() => setEditing(true)} /></Tooltip>
                <Tooltip title="Refrescar"><Button size='small' icon={<ReloadOutlined />} onClick={loadMetrics} loading={loading} /></Tooltip>
                <Tooltip title={expanded ? 'Colapsar' : 'Expandir'}><Button size='small' icon={expanded ? <UpOutlined /> : <DownOutlined />} onClick={() => onExpandChange(ipConfig.ip)} /></Tooltip>
                <Popconfirm title='Eliminar tarjeta' description='¿Estás seguro?' onConfirm={() => onDelete(ipConfig.ip)}>
                  <Tooltip title="Eliminar"><Button size='small' danger icon={<DeleteOutlined />} /></Tooltip>
                </Popconfirm>
              </>
            )}
          </div>
        </div>
      }
    >
      {loading ? (
        <Spin />
      ) : error ? (
        <Alert message={error} type='error' style={{ marginBottom: 16 }} />
      ) : !metrics ? (
        <Empty description='Cargando métricas...' />
      ) : expanded ? (
        <Space direction='vertical' style={{ width: '100%' }} size='large'>
          {metrics.system && (
            <>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0c4a6e', marginBottom: 12 }}>
                  📊 SISTEMA OPERATIVO
                </div>
                <MetricDisplay
                  label='Uptime'
                  value={metrics.system.uptime_human || metrics.host?.uptime_human || 'N/A'}
                  color='#2563eb'
                />
                <MetricDisplay
                  label='Carga de CPU (1m)'
                  value={metrics.host?.load_1m?.toFixed(2) || 'N/A'}
                  unit='%'
                  color='#059669'
                />
                <MetricDisplay
                  label='Memoria Libre'
                  value={metrics.host?.free_mem_pct?.toFixed(1) || 'N/A'}
                  unit='%'
                  color='#7c3aed'
                />
              </div>
              <Divider />
            </>
          )}

          {metrics.services && (
            <>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0c4a6e', marginBottom: 12 }}>
                  🔧 SERVICIOS
                </div>
                {Object.entries(metrics.services).map(([service, status]) => (
                  <MetricDisplay
                    key={service}
                    label={service}
                    value={status ? '✓ Activo' : '✗ Inactivo'}
                    color={status ? '#059669' : '#dc2626'}
                  />
                ))}
              </div>
              <Divider />
            </>
          )}

          {metrics.network && (
            <>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0c4a6e', marginBottom: 12 }}>
                  🌐 RED
                </div>
                {Object.entries(metrics.network)
                  .filter(([_, value]) => typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean')
                  .map(([key, value]) => (
                    <MetricDisplay
                      key={key}
                      label={key}
                      value={typeof value === 'number' ? value.toFixed(2) : String(value)}
                    />
                  ))}
              </div>
              <Divider />
            </>
          )}

          {metrics.mqtt && (
            <>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0c4a6e', marginBottom: 12 }}>
                  📡 MQTT BROKER
                </div>
                <MetricDisplay
                  label='Estado'
                  value={metrics.mqtt.connected ? '🟢 Conectado' : '🔴 Desconectado'}
                  color={metrics.mqtt.connected ? '#059669' : '#dc2626'}
                />
                {metrics.mqtt.connected && (
                  <>
                    <MetricDisplay label='Clientes' value={metrics.mqtt.clients || 'N/A'} />
                    <MetricDisplay label='Mensajes/s' value={metrics.mqtt.messages_sec || 'N/A'} />
                  </>
                )}
              </div>
            </>
          )}

          {advancedPairs.length > 0 && (
            <>
              <Divider />
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0c4a6e', marginBottom: 12 }}>
                  🧪 RECURSOS AVANZADOS
                </div>
                {advancedPairs.map(([key, value]) => (
                  (() => {
                    const formatted = formatMetricFromEntry([key, value]);
                    return (
                      <MetricDisplay
                        key={key}
                        label={prettifyMetricLabel(key)}
                        value={formatted.value}
                        unit={formatted.unit}
                      />
                    );
                  })()
                ))}
              </div>
            </>
          )}

          {metrics.host && (
            <>
              <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
                IP: {ipConfig.ip} | Fuente: {metrics.host.uptime_source || 'local'}
              </div>
            </>
          )}
        </Space>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 4,
        }}>
          <CompactTile
            label='Uptime'
            value={metrics.system?.uptime_human?.split(',')[0] || metrics.host?.uptime_human || 'N/A'}
            bg='#f0fdf4'
            color='#059669'
          />
          <CompactTile
            label='CPU °C'
            value={cpuTemp.value}
            unit={cpuTemp.value !== 'N/A' && !cpuTemp.unit ? '°C' : cpuTemp.unit}
            bg='#eff6ff'
            color='#2563eb'
          />
          <CompactTile
            label='RAM'
            value={ramDisplay.value !== 'N/A' ? ramDisplay.value : (metrics.host?.free_mem_pct?.toFixed(1) || 'N/A')}
            unit={ramDisplay.value !== 'N/A' ? ramDisplay.unit : (metrics.host?.free_mem_pct !== undefined ? '%' : '')}
            bg='#fef3c7'
            color='#d97706'
          />
          <CompactTile
            label='Disco'
            value={diskDisplay.value}
            unit={diskDisplay.unit}
            bg='#fff7ed'
            color='#c2410c'
          />
          <CompactTile
            label='CPU 1m'
            value={metrics.host?.load_1m?.toFixed(2) || 'N/A'}
            bg='#e0f2fe'
            color='#0369a1'
          />
          <CompactTile
            label='Estado'
            value={isOnline ? 'Activo' : 'Inactivo'}
            bg={isOnline ? '#dcfce7' : '#fee2e2'}
            color={isOnline ? '#059669' : '#dc2626'}
          />
        </div>
      )}
    </Card>
  );
}

export default function IndustrialMonitoringDashboard() {
  const [config, setConfig] = React.useState([]);
  const [expandedIps, setExpandedIps] = React.useState({});
  const screens = useBreakpoint();
  const isMobile = !screens.md;


  // Cargar config desde backend
  React.useEffect(() => {
    let mounted = true;
    fetchDashboardConfig()
      .then((data) => {
        if (!mounted) return;
        // Si no hay tarjetas, inicializar con defaults SOLO si no hay nada guardado
        let cards = Array.isArray(data.cards) ? data.cards : [];
        if (cards.length === 0) {
          cards = INDUSTRIAL_IPS.map((ip) => ({
            ip: ip.ip,
            displayName: ip.defaultName,
            defaultName: ip.defaultName,
            color: '#f8fafc',
            id: ip.ip,
          }));
        } else {
          // Si hay tarjetas guardadas, asegurar que todas tengan color y defaultName
          cards = cards.map((item) => {
            const def = INDUSTRIAL_IPS.find((d) => d.ip === item.ip);
            return {
              ...item,
              defaultName: def?.defaultName || item.displayName || item.ip,
              color: item.color || '#f8fafc',
            };
          });
        }
        setConfig(cards);
      })
      .catch(() => {
        setConfig(
          INDUSTRIAL_IPS.map((ip) => ({
            ip: ip.ip,
            displayName: ip.defaultName,
            defaultName: ip.defaultName,
            color: '#f8fafc',
            id: ip.ip,
          }))
        );
      });
    return () => { mounted = false; };
  }, []);



  // Guardar config en backend solo si el usuario hizo un cambio real
  const saveConfig = React.useCallback((nextConfig) => {
    if (nextConfig.length > 0) {
      saveDashboardConfig({ version: 1, cards: nextConfig }).catch(() => {});
    }
  }, []);

  const handleNameChange = (ip, newName, color) => {
    const cleanName = String(newName || '').trim();
    setConfig((prev) => {
      const next = prev.map((item) =>
        item.ip === ip ? { ...item, displayName: cleanName || item.defaultName, color: color || item.color || '#f8fafc' } : item
      );
      saveConfig(next);
      return next;
    });
  };

  const handleColorChange = (ip, color) => {
    setConfig((prev) => {
      const next = prev.map((item) =>
        item.ip === ip ? { ...item, color } : item
      );
      saveConfig(next);
      return next;
    });
  };

  const handleDelete = (ip) => {
    setConfig((prev) => {
      const next = prev.filter((item) => item.ip !== ip);
      saveConfig(next);
      return next;
    });
  };

  const handleExpandChange = (ip) => {
    setExpandedIps((prev) => ({
      ...prev,
      [ip]: !prev[ip],
    }));
  };

  return (
    <div style={{ width: '100%' }}>
      <div style={{ marginBottom: isMobile ? 8 : 12 }}>
        <h2 style={{ marginBottom: 8, fontSize: isMobile ? 16 : 20 }}>🏭 Centro de Monitoreo Industrial</h2>
        <Alert
          type='info'
          showIcon
          message='Las tarjetas se actualizan cada 30 segundos. Usa el botón de color para personalizar y editar el nombre.'
          style={{ marginBottom: 8, fontSize: isMobile ? 12 : 13 }}
        />
      </div>

      {config.length === 0 ? (
        <Card size='small'>
          <Empty description='No hay servidores configurados' />
        </Card>
      ) : (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: isMobile ? 8 : 12,
          width: '100%',
        }}>
          {config.map((ipConfig) => (
            <div
              key={ipConfig.ip}
              style={{
                flex: isMobile ? '0 0 100%' : '0 0 calc(33.333% - 7px)',
                minWidth: 0,
              }}
            >
              <MonitoringCard
                ipConfig={ipConfig}
                onNameChange={handleNameChange}
                onDelete={handleDelete}
                expanded={expandedIps[ipConfig.ip] || false}
                onExpandChange={handleExpandChange}
                onColorChange={handleColorChange}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
